/**
 * CollectionManager Component
 *
 * Full-featured collection management: create, edit, delete collections.
 * Part of Phase 2: Collections & Personas.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FolderPlus,
  FolderOpen,
  Trash2,
  Edit2,
  Save,
  X,
  Loader2,
  AlertCircle,
  Check,
  Plus
} from 'lucide-react';
import { useAuth } from '../auth';
import { useScope } from '../../hooks/useScope';
import {
  createCollection,
  updateCollection,
  deleteCollection,
  createCollectionFromSession
} from '../../services/collectionsApi';
import ConfirmDialog from '../ui/ConfirmDialog';
import ErrorDisplay from '../ui/ErrorDisplay';

// Color presets
const COLOR_PRESETS = [
  '#8B5CF6', // Purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
];

// ============================================
// Create Collection Modal
// ============================================

const CreateCollectionModal = ({
  isOpen,
  onClose,
  onSuccess,
  sessionId = null
}) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLOR_PRESETS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let collection;

      if (sessionId) {
        // Create from import session
        collection = await createCollectionFromSession(user, sessionId, {
          name: name.trim(),
          description: description.trim() || null,
          color,
        });
      } else {
        // Create empty collection
        collection = await createCollection(user, {
          name: name.trim(),
          description: description.trim() || null,
          color,
        });
      }

      onSuccess?.(collection);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create collection');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-surface-elevated border border-white/10 rounded-xl w-full max-w-md p-6 m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <FolderPlus className="h-5 w-5 text-primary-400" />
            {sessionId ? 'Save as Collection' : 'Create Collection'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Name <span className="text-error-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Collection"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Color
            </label>
            <div className="flex gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg transition-transform ${
                    color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-110' : ''
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-600/50 text-white rounded-lg transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Create
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Edit Collection Modal
// ============================================

const EditCollectionModal = ({
  isOpen,
  onClose,
  collection,
  onSuccess
}) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLOR_PRESETS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form when collection changes
  useEffect(() => {
    if (collection) {
      setName(collection.name || '');
      setDescription(collection.description || '');
      setColor(collection.color || COLOR_PRESETS[0]);
    }
  }, [collection]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updated = await updateCollection(user, collection.id, {
        name: name.trim(),
        description: description.trim() || null,
        color,
      });

      onSuccess?.(updated);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update collection');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !collection) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-surface-elevated border border-white/10 rounded-xl w-full max-w-md p-6 m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-primary-400" />
            Edit Collection
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Name <span className="text-error-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Collection"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Color
            </label>
            <div className="flex gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg transition-transform ${
                    color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-110' : ''
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-600/50 text-white rounded-lg transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Collection Manager Component
// ============================================

const CollectionManager = () => {
  const { user } = useAuth();
  const { collections, refresh, selectCollection, loading } = useScope();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (!collectionToDelete) return;
    const collectionId = collectionToDelete.id;
    setDeletingId(collectionId);
    try {
      await deleteCollection(user, collectionId);
      setCollectionToDelete(null);
      refresh();
    } catch (error) {
      console.error('Failed to delete collection:', error);
      setError(error.message || 'Failed to delete collection');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateSuccess = useCallback((collection) => {
    refresh();
    if (collection?.id) {
      selectCollection(collection.id);
    }
  }, [refresh, selectCollection]);

  const handleEditSuccess = useCallback(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="glass rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-primary-400" />
          Collections
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors min-h-[44px]"
        >
          <Plus className="h-4 w-4" />
          New Collection
        </button>
      </div>

      {error && (
        <ErrorDisplay
          error={error}
          onDismiss={() => setError(null)}
          dismissible
          inline
          className="mb-4"
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary-400" />
        </div>
      ) : collections.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary-500/10 border border-primary-500/20 flex items-center justify-center mb-4">
            <FolderOpen className="h-8 w-8 text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No collections yet</h3>
          <p className="text-sm text-gray-400 max-w-xs mb-6">
            Group related projects by client, technology, or investment thesis.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create your first collection
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {collections.map((collection) => (
            <div
              key={collection.id}
              className="flex items-center gap-3 p-4 glass rounded-lg hover:bg-white/10 transition-colors group"
            >
              <div
                className="h-10 w-10 rounded-lg flex-shrink-0"
                style={{ backgroundColor: collection.color || '#8B5CF6' }}
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium truncate">{collection.name}</h3>
                <p className="text-sm text-gray-500">
                  {collection.project_count || 0} projects
                  {collection.description && ` • ${collection.description}`}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => setEditingCollection(collection)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Edit"
                  aria-label={`Edit collection ${collection.name}`}
                >
                  <Edit2 className="h-4 w-4 text-gray-400" />
                </button>
                <button
                  onClick={() => setCollectionToDelete(collection)}
                  disabled={deletingId === collection.id}
                  className="p-2 hover:bg-error-500/10 rounded-lg transition-colors"
                  title="Delete"
                  aria-label={`Delete collection ${collection.name}`}
                >
                  {deletingId === collection.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-gray-400 hover:text-error-400" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateCollectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit Modal */}
      <EditCollectionModal
        isOpen={!!editingCollection}
        onClose={() => setEditingCollection(null)}
        collection={editingCollection}
        onSuccess={handleEditSuccess}
      />

      <ConfirmDialog
        isOpen={Boolean(collectionToDelete)}
        onConfirm={handleDelete}
        onCancel={() => setCollectionToDelete(null)}
        title="Delete collection?"
        message={`Delete "${collectionToDelete?.name || 'this collection'}"? Projects will remain available outside the collection.`}
        confirmLabel="Delete collection"
        variant="danger"
        loading={Boolean(collectionToDelete) && deletingId === collectionToDelete.id}
      />
    </div>
  );
};

// Export both the manager and the create modal for use elsewhere
export { CreateCollectionModal, EditCollectionModal, COLOR_PRESETS };
export default CollectionManager;
